<div class="report-builder">

	<!-- HOME -->
	@if (step === -1) {
		<div class="d-flex gap-3 flex-wrap">
			<div class="rb-card">
				<h3>🆕 {{ 'reportBuilder.home.newTitle' | translate }}</h3>
				<p class="rb-muted">{{ 'reportBuilder.home.newDesc' | translate }}</p>
				<button class="rb-btn rb-btn-primary" (click)="goStep(0)">{{ 'reportBuilder.home.create' | translate }}</button>
			</div>
			<div class="rb-card rb-card-wide">
				<h3>📁 {{ 'reportBuilder.home.savedTitle' | translate }}</h3>
				@if (!saved.length) {
					<p class="rb-muted">{{ 'reportBuilder.home.noSaved' | translate }}</p>
				}
				@for (r of saved; track r.name; let i = $index) {
					<div class="rb-saved-item" (click)="loadReport(r)">
						<div>
							<div class="rb-saved-name"><svg-icon [src]="entityIconSrc(r.entity)" [svgStyle]="{ 'width.px': 18, 'height.px': 18 }" style="--svg-color: currentColor; display: inline-flex; vertical-align: middle"></svg-icon> {{ r.name }}</div>
							<div class="rb-saved-meta">{{ 'reportBuilder.home.fieldsCount' | translate: { count: r.fields.length } }} · {{ 'reportBuilder.home.filtersCount' | translate: { count: r.filters.length } }} · {{ entityKey(r.entity) | translate }}</div>
						</div>
						<div class="d-flex gap-2 align-items-center">
							<button class="rb-btn rb-btn-primary rb-btn-sm" (click)="$event.stopPropagation(); loadReport(r)">{{ 'reportBuilder.home.run' | translate }}</button>
							<button class="rb-btn rb-btn-danger rb-btn-sm" (click)="deleteSaved(i, $event)">✕</button>
						</div>
					</div>
				}
			</div>
		</div>
	}

	<!-- WIZARD -->
	@if (step !== -1) {

		<div class="rb-steps">
			@for (label of ['reportBuilder.steps.entity', 'reportBuilder.steps.fields', 'reportBuilder.steps.filters', 'reportBuilder.steps.result']; track label; let i = $index) {
				<button
					class="rb-step-btn"
					[class.rb-step-active]="step === i"
					[class.rb-step-done]="step > i"
					(click)="goStepSafe(i)"
				>{{ step > i ? '✓ ' : '' }}{{ label | translate }}</button>
			}
		</div>

		<!-- STEP 0: ENTITY -->
		@if (step === 0) {
			<div class="rb-card">
				<p class="rb-muted">{{ 'reportBuilder.entity.select' | translate }}</p>
				<div class="d-flex gap-3 flex-wrap">
					@for (key of entityKeys; track key) {
						<button class="rb-entity-btn" [class.rb-entity-sel]="entity === key" (click)="selectEntity(key)">
							<svg-icon [src]="entityIconSrc(key)" [svgStyle]="{ 'width.px': 30, 'height.px': 30 }" style="--svg-color: currentColor" class="d-flex"></svg-icon>
							<div>
								<div>{{ entityKey(key) | translate }}</div>
								<div class="rb-entity-meta">{{ 'reportBuilder.entity.meta' | translate: { categories: entityCategoryCount(key), fields: entityFieldCount(key) } }}</div>
							</div>
						</button>
					}
				</div>
			</div>
		}

		<!-- STEP 1: FIELDS -->
		@if (step === 1 && currentSchema && entity) {
			<div class="rb-card">
				<div class="d-flex justify-content-between align-items-center mb-2">
					<p class="rb-muted m-0">{{ 'reportBuilder.fields.title' | translate: { entity: (entityKey(entity) | translate) } }}</p>
					<span class="rb-field-count">{{ 'reportBuilder.fields.selected' | translate: { count: fields.length } }}</span>
				</div>
				<div style="max-height: 500px; overflow-y: auto; padding-right: 8px">
					@for (group of currentSchema.groups | keyvalue: originalOrder; track group.key) {
						<div class="rb-group-title">
							<span>{{ groupKey(group.key) | translate }}</span>
							<button (click)="toggleGroup(group.value)">{{ (isGroupFullySelected(group.value) ? 'reportBuilder.fields.removeAll' : 'reportBuilder.fields.selectAll') | translate }}</button>
						</div>
						<div class="rb-chips">
							@for (f of group.value; track f) {
								<span class="rb-chip" [class.rb-chip-sel]="isFieldSelected(f)" (click)="toggleField(f)">
									{{ isFieldSelected(f) ? '✓ ' : '' }}{{ fieldKey(f) | translate }}
								</span>
							}
						</div>
					}

					@if ((currentSchema.oneN | keyvalue).length > 0) {
						<div class="rb-section-title">{{ 'reportBuilder.fields.historicalSection' | translate }}</div>
					}

					@for (sub of currentSchema.oneN | keyvalue: originalOrder; track sub.key) {
						<div class="rb-group-title">
							<span>{{ groupKey(sub.key) | translate }} <span class="rb-badge-1n">{{ 'reportBuilder.fields.oneNBadge' | translate }}</span></span>
						</div>
						<div class="rb-mode-row">
							<button class="rb-mode-btn" [class.rb-mode-sel]="oneNMode(sub.key) === 'none'" (click)="setOneNMode(sub.key, 'none')">{{ 'reportBuilder.fields.modeNone' | translate }}</button>
							<button class="rb-mode-btn" [class.rb-mode-sel]="oneNMode(sub.key) === 'summary'" (click)="setOneNMode(sub.key, 'summary')">📊 {{ 'reportBuilder.fields.modeSummary' | translate }}</button>
							<button
								class="rb-mode-btn"
								[class.rb-mode-sel]="oneNMode(sub.key) === 'detail'"
								[disabled]="!canUseDetailMode(sub.key)"
								[title]="!canUseDetailMode(sub.key) ? ('reportBuilder.fields.onlyOneDetail' | translate) : ''"
								(click)="setOneNMode(sub.key, 'detail')"
							>📋 {{ 'reportBuilder.fields.modeDetail' | translate }}</button>
						</div>

						@if (oneNMode(sub.key) === 'summary') {
							<div class="rb-mode-info">{{ 'reportBuilder.fields.summaryInfo' | translate: { entity: (entityKey(entity) | translate | lowercase) } }}</div>
							<div class="d-flex justify-content-end mb-1">
								<button class="rb-link-btn" [class.rb-link-danger]="isGroupFullySelected(sub.value.summary)" (click)="toggleGroup(sub.value.summary)">
									{{ (isGroupFullySelected(sub.value.summary) ? 'reportBuilder.fields.removeAll' : 'reportBuilder.fields.selectAll') | translate }}
								</button>
							</div>
							<div class="rb-chips">
								@for (f of sub.value.summary; track f) {
									<span class="rb-chip" [class.rb-chip-sel]="isFieldSelected(f)" (click)="toggleField(f)">
										{{ isFieldSelected(f) ? '✓ ' : '' }}{{ fieldKey(f) | translate }}
									</span>
								}
							</div>
						}

						@if (oneNMode(sub.key) === 'detail') {
							<div class="rb-mode-info">⚠️ {{ 'reportBuilder.fields.detailInfo' | translate: { group: (groupKey(sub.key) | translate | lowercase), entity: (entityKey(entity) | translate | lowercase) } }}</div>
							<div class="d-flex justify-content-end mb-1">
								<button class="rb-link-btn" [class.rb-link-danger]="isGroupFullySelected(sub.value.detail)" (click)="toggleGroup(sub.value.detail)">
									{{ (isGroupFullySelected(sub.value.detail) ? 'reportBuilder.fields.removeAll' : 'reportBuilder.fields.selectAll') | translate }}
								</button>
							</div>
							<div class="rb-chips">
								@for (f of sub.value.detail; track f) {
									<span class="rb-chip" [class.rb-chip-sel]="isFieldSelected(f)" (click)="toggleField(f)">
										{{ isFieldSelected(f) ? '✓ ' : '' }}{{ fieldKey(f) | translate }}
									</span>
								}
							</div>
						}
					}
				</div>
			</div>
		}

		<!-- STEP 2: FILTERS -->
		@if (step === 2 && currentSchema) {
			<div class="rb-card">
				<p class="rb-muted">{{ 'reportBuilder.filters.title' | translate }} <span class="rb-optional">{{ 'reportBuilder.filters.optional' | translate }}</span>:</p>
				@for (f of filters; track $index; let i = $index) {
					<div class="d-flex gap-2 align-items-center mb-2 flex-wrap">
						<p-select
							[options]="filterFieldOptions()"
							optionLabel="label"
							optionValue="value"
							[(ngModel)]="f.field"
							(onChange)="onFilterFieldChange(f)"
							[placeholder]="'reportBuilder.filters.fieldPlaceholder' | translate"
							[style]="{ minWidth: '180px' }"
						></p-select>
						<p-select
							[options]="filterOperatorOptions(f.field)"
							optionLabel="label"
							optionValue="value"
							[(ngModel)]="f.op"
							[style]="{ minWidth: '100px' }"
						></p-select>
						@if (f.field && isDateField(f.field)) {
							<p-datepicker [(ngModel)]="f.value" dateFormat="dd/mm/yy" [style]="{ width: '160px' }"></p-datepicker>
						} @else if (f.field && isBooleanField(f.field)) {
							<p-select
								[options]="booleanOptions()"
								optionLabel="label"
								optionValue="value"
								[(ngModel)]="f.value"
								[placeholder]="'reportBuilder.filters.valuePlaceholder' | translate"
								[style]="{ width: '180px' }"
							></p-select>
						} @else {
							<input type="text" pInputText [placeholder]="'reportBuilder.filters.valuePlaceholder' | translate" [(ngModel)]="f.value" style="width: 180px" />
						}
						<button class="rb-btn rb-btn-danger rb-btn-sm" (click)="removeFilter(i)">✕</button>
					</div>
				}
				<button class="rb-btn rb-btn-secondary rb-btn-sm" (click)="addFilter()">{{ 'reportBuilder.filters.add' | translate }}</button>
			</div>
		}

		<!-- STEP 3: RESULT -->
		@if (step === 3 && currentSchema && entity) {
			<div class="rb-card">
				<div class="d-flex gap-2 align-items-center mb-3 flex-wrap">
					<input type="text" pInputText [placeholder]="'reportBuilder.result.namePlaceholder' | translate" [(ngModel)]="reportName" style="flex: 1; min-width: 200px" />
					<button class="rb-btn rb-btn-primary" (click)="saveReport()">💾 {{ 'reportBuilder.result.save' | translate }}</button>
					<button class="rb-btn rb-btn-primary" (click)="generatePreview()">▶ {{ 'reportBuilder.result.generate' | translate }}</button>
				</div>
				<div class="d-flex gap-3 flex-wrap mb-2">
					<div class="rb-summary-item"><strong>{{ 'reportBuilder.result.entity' | translate }}</strong>&ngsp;<svg-icon [src]="entityIconSrc(entity)" [svgStyle]="{ 'width.px': 18, 'height.px': 18 }" style="--svg-color: currentColor; display: inline-flex; vertical-align: middle"></svg-icon> {{ entityKey(entity) | translate }}</div>
					<div class="rb-summary-item"><strong>{{ 'reportBuilder.result.fields' | translate }}</strong> {{ fields.length }}</div>
					<div class="rb-summary-item"><strong>{{ 'reportBuilder.result.filters' | translate }}</strong> {{ activeFilters.length }}</div>
					@if (detailEntity) {
						<div class="rb-summary-item"><strong>{{ 'reportBuilder.result.detail' | translate }}</strong> {{ groupKey(detailEntity) | translate }}</div>
					}
				</div>
				@if (activeFilters.length) {
					<div class="rb-muted mb-2">
						<strong>{{ 'reportBuilder.result.filters' | translate }} </strong>
						@for (f of activeFilters; track f.field) {
							<span class="rb-badge">{{ fieldKey(f.field) | translate }} {{ f.op === 'contiene' ? ('reportBuilder.filters.opContains' | translate) : f.op }} {{ filterValueText(f) }}</span>
						}
					</div>
				}
				<div class="rb-muted"><strong>{{ 'reportBuilder.result.fields' | translate }}</strong> {{ fieldsText() }}</div>
			</div>

			@if (showPreview) {
				<div class="rb-card">
					<div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
						<h3 class="m-0">{{ reportName || ('reportBuilder.result.defaultName' | translate) }} — {{ 'reportBuilder.result.count' | translate: { shown: previewRows.length, total: previewTotalCount } }}</h3>
						<div class="d-flex gap-2">
							<app-table-excel-button class="buttonTableExcel" [isDisabled]="!previewRows.length"
								(btnClick)="exportExcel()"></app-table-excel-button>
							<app-table-pdf-button pTooltip="{{ 'reports.exportPDF' | translate }}" tooltipPosition="top" class="buttonTableExcel"
								[isDisabled]="!previewRows.length" (btnClick)="exportPdf()"></app-table-pdf-button>
						</div>
					</div>
					@if (!loadingPreview && previewRows.length < previewTotalCount) {
						<div class="rb-mode-info">⚠️ {{ 'reportBuilder.result.truncated' | translate: { shown: previewRows.length, total: previewTotalCount } }}</div>
					}
					@if (loadingPreview) {
						<div class="rb-empty"><span>⏳</span>{{ 'reportBuilder.result.generating' | translate }}</div>
					} @else if (!previewRows.length) {
						<div class="rb-empty"><span>🔍</span>{{ 'reportBuilder.result.empty' | translate }}</div>
					} @else {
						<div class="rb-table-wrap">
							<p-table [value]="previewRows" [paginator]="true" [rows]="50" [rowsPerPageOptions]="[25, 50, 100, 200]"
								styleClass="p-datatable-striped" [scrollable]="true" scrollHeight="60vh">
								<ng-template pTemplate="header">
									<tr>
										@for (f of fields; track f) {
											<th>{{ fieldKey(f) | translate }}</th>
										}
									</tr>
								</ng-template>
								<ng-template pTemplate="body" let-row>
									<tr>
										@for (f of fields; track f) {
											<td>{{ row[f] }}</td>
										}
									</tr>
								</ng-template>
							</p-table>
						</div>
					}
				</div>
			}
		}

		<div class="rb-nav">
			<button class="rb-btn rb-btn-secondary" (click)="goHome()">← {{ 'reportBuilder.nav.home' | translate }}</button>
			<div class="d-flex gap-2">
				@if (step > 0) {
					<button class="rb-btn rb-btn-secondary" (click)="goStep(step - 1)">← {{ 'reportBuilder.nav.previous' | translate }}</button>
				}
				@if (step < 3) {
					<button class="rb-btn rb-btn-primary" [disabled]="!canGoNext()" (click)="canGoNext() && goStep(step + 1)">{{ 'reportBuilder.nav.next' | translate }} →</button>
				}
			</div>
		</div>
	}

</div>
